import React, { useState, useEffect } from "react";
import { Button, Form, Input, Picker, Toast, NavBar } from 'antd-mobile';
import { useLocation } from 'react-router-dom';
import type { PickerValue } from "antd-mobile/es/components/picker";
import { t } from "../../../../i18n";
import { getNavBarContainerStyle, isIOS, isAndroid } from "../../../../utils/deviceUtils";
import { popBack } from "../../../../utils/appRuntime";
import { loginWithVerificationCode } from "../../../../utils/auth";
import { getAppVersion } from "../../../../utils/appRuntime";
import { useSendVerificationCode } from '../../../../hooks/useSendVerificationCode';
import { validatePhoneNumber } from '../../../../utils/phoneValidation';
import { validateVerificationCodeFormat } from '../../../../utils/verificationCodeValidation';
import { useNativeLangSync } from '../../../../utils/langUtils';

const countryCodes = [
    { label: '+1', value: '+1', flag: '🇺🇸' },
];

const normalizeCountryCode = (countryCode?: string | null): string | null => {
    if (!countryCode) {
        return null;
    }
    const trimmed = countryCode.trim();
    if (!trimmed) {
        return null;
    }
    return trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
};

const resolveLoginAccount = (location: ReturnType<typeof useLocation>): {
    countryCode: string;
    phoneOrEmail: string;
} | null => {
    const state = location.state as { countryCode?: string; phoneOrEmail?: string } | null;
    const stateCountryCode = normalizeCountryCode(state?.countryCode);
    const statePhone = state?.phoneOrEmail?.trim();
    if (stateCountryCode && statePhone) {
        return {
            countryCode: stateCountryCode,
            phoneOrEmail: statePhone,
        };
    }

    const searchParams = new URLSearchParams(location.search);
    const queryCountryCode = normalizeCountryCode(searchParams.get('countryCode'));
    const queryPhone = searchParams.get('phone')?.trim() || searchParams.get('phoneOrEmail')?.trim();
    if (queryCountryCode && queryPhone) {
        return {
            countryCode: queryCountryCode,
            phoneOrEmail: queryPhone,
        };
    }

    return null;
};

/**
 * 原生 WebView 专用黑名单短信验证（与 {@link BlacklistSmsVerificationPage} 逻辑一致，返回走 nativePopBack）。
 */
const BlacklistSmsVerificationNativePage: React.FC = () => {
    useNativeLangSync();
    const [form] = Form.useForm();
    const location = useLocation();
    const [verificationCode, setVerificationCode] = useState('');
    const [pickerVisible, setPickerVisible] = useState(false);
    const [selectedCode, setSelectedCode] = useState<string>('+1');

    const [loginAccount, setLoginAccount] = useState<{
        countryCode: string;
        phoneOrEmail: string;
    } | null>(null);

    const { sendCode: sendLoginVerificationCode, countdown } = useSendVerificationCode({
        type: 'login',
        getPhone: () => ({
            phone: form.getFieldValue('phoneOrEmail'),
            countryCode: form.getFieldValue('countryCode') || selectedCode
        }),
        onDisabled: () => Toast.show(t('errors.business.account.disabled')),
    });

    useEffect(() => {
        const resolvedLoginAccount = resolveLoginAccount(location);
        if (!resolvedLoginAccount) {
            popBack();
            return;
        }

        setLoginAccount(resolvedLoginAccount);
        form.setFieldsValue({
            countryCode: resolvedLoginAccount.countryCode,
            phoneOrEmail: ''
        });
        setSelectedCode(resolvedLoginAccount.countryCode);
    }, [location, form]);

    const onVerificationCodeLogin = async (values: { countryCode: string; phoneOrEmail: string }) => {
        try {
            const phoneValidation = validatePhoneNumber(values.phoneOrEmail, values.countryCode);
            if (!phoneValidation.isValid) {
                Toast.show(phoneValidation.errorMessage!);
                return;
            }

            if (loginAccount) {
                const loginPhoneDigits = loginAccount.phoneOrEmail.replace(/\D/g, '');
                const inputPhoneDigits = phoneValidation.phoneDigits!;

                const loginCountryCode = Number(loginAccount.countryCode.replace('+', ''));
                const inputCountryCode = Number(values.countryCode.replace('+', ''));

                if (loginPhoneDigits !== inputPhoneDigits || loginCountryCode !== inputCountryCode) {
                    Toast.show(t('pages.login.blacklistSmsVerification.phoneMismatch'));
                    return;
                }
            }

            const codeValidation = validateVerificationCodeFormat(verificationCode);
            if (!codeValidation.isValid) {
                Toast.show(codeValidation.errorMessage!);
                return;
            }

            const platform = isIOS() ? 'iOS' : isAndroid() ? 'Android' : 'Web';
            const versionName = await getAppVersion();
            const result = await loginWithVerificationCode(
                Number(values.countryCode.replace('+', '')),
                phoneValidation.phoneDigits!,
                verificationCode.trim(),
                undefined,
                true,
                platform,
                versionName
            );

            if (result.userInfo) {
                popBack();
            } else {
                const messageKey = result.messageKey;
                if (messageKey === 'errors.validation.invalidPhoneNumber') {
                    Toast.show(t('errors.validation.invalidPhoneNumber'));
                } else if (messageKey === 'errors.business.account.verificationCode.invalid') {
                    Toast.show(t('common.verificationCode.invalid'));
                } else if (messageKey === 'errors.business.account.verificationCode.notAllowedForRole') {
                    Toast.show(t('errors.business.account.verificationCode.notAllowedForRole'));
                } else if (messageKey === 'errors.business.account.disabled') {
                    Toast.show(t('errors.business.account.disabled'));
                } else {
                    Toast.show(t('errors.login.failed'));
                }
            }
        } catch (error: any) {
            Toast.show(t('errors.login.failed'));
        }
    };

    const handleGetVerificationCode = async () => {
        const currentPhone = form.getFieldValue('phoneOrEmail');
        const currentCountryCode = form.getFieldValue('countryCode') || selectedCode;

        const phoneValidation = validatePhoneNumber(currentPhone, currentCountryCode);
        if (!phoneValidation.isValid) {
            Toast.show(phoneValidation.errorMessage!);
            return;
        }

        if (loginAccount) {
            const loginPhoneDigits = loginAccount.phoneOrEmail.replace(/\D/g, '');
            const inputPhoneDigits = phoneValidation.phoneDigits!;

            const loginCountryCode = Number(loginAccount.countryCode.replace('+', ''));
            const inputCountryCode = Number(currentCountryCode.replace('+', ''));

            if (loginPhoneDigits !== inputPhoneDigits || loginCountryCode !== inputCountryCode) {
                Toast.show(t('pages.login.blacklistSmsVerification.phoneMismatch'));
                return;
            }
        }

        await sendLoginVerificationCode();
    };

    const onFinish = async (values: any) => {
        await onVerificationCodeLogin(values);
    };

    return (
        <div className="min-h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar
                    onBack={popBack}
                    style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        background: '#fff',
                    }}
                >
                    {t('pages.login.blacklistSmsVerification.title')}
                </NavBar>
            </div>
            <div className="flex flex-col p-4 gap-8">
                <Form
                    form={form}
                    onFinish={onFinish}
                    initialValues={{ countryCode: '+1' }}
                    layout="vertical"
                    style={{
                        '--border-top': 'none',
                        '--border-inner': 'none',
                        '--border-bottom': 'none',
                    }}
                >
                    <div className="text-lg font-semibold mb-2">
                        {t('pages.login.blacklistSmsVerification.phoneNumberLabel')}
                        <span className="text-red-500">*</span>
                    </div>

                    <div className="flex gap-2 mb-4">
                        <Form.Item name="countryCode" className="mb-0 w-[70px]">
                            <div
                                className="w-full h-full flex items-center justify-center gap-1 cursor-pointer"
                                onClick={() => setPickerVisible(true)}
                            >
                                <span className="text-lg">
                                    {countryCodes.find(c => c.value === selectedCode)?.flag || '🇺🇸'}
                                </span>
                                <span>{selectedCode}</span>
                            </div>
                        </Form.Item>

                        <Form.Item
                            name="phoneOrEmail"
                            className="mb-0 flex-1"
                        >
                            <Input
                                clearable
                                placeholder={t('pages.login.phoneNumber')}
                                type="tel"
                                inputMode="tel"
                                value={form.getFieldValue('phoneOrEmail') || ''}
                                onChange={val => {
                                    const digitsOnly = val.replace(/\D/g, '');
                                    const maxLength = selectedCode === '+86' ? 11 : 10;
                                    form.setFieldValue('phoneOrEmail', digitsOnly.slice(0, maxLength));
                                }}
                            />
                        </Form.Item>
                    </div>

                    <Picker
                        columns={[countryCodes.map(code => ({
                            label: `${code.flag} ${code.label}`,
                            value: code.value
                        }))]}
                        visible={pickerVisible}
                        value={[selectedCode]}
                        onClose={() => setPickerVisible(false)}
                        onConfirm={(val: PickerValue[]) => {
                            const newCode = String(val[0]);
                            setSelectedCode(newCode);
                            form.setFieldsValue({ countryCode: newCode });
                            setPickerVisible(false);
                        }}
                    />

                    <div className="text-lg font-semibold mb-2">
                        {t('pages.login.blacklistSmsVerification.verificationCodeLabel')}
                        <span className="text-red-500">*</span>
                    </div>

                    <Form.Item className="w-full mb-4">
                        <div className="adm-input">
                            <Input
                                className="adm-input-element"
                                placeholder={t('common.verificationCode.placeholder')}
                                type="tel"
                                inputMode="numeric"
                                value={verificationCode}
                                onChange={val => {
                                    const digitsOnly = val.replace(/\D/g, '').slice(0, 6);
                                    setVerificationCode(digitsOnly);
                                }}
                            />
                            <div className="px-2 whitespace-nowrap flex-shrink-0">
                                <Button
                                    size="small"
                                    color="primary"
                                    fill="none"
                                    disabled={countdown > 0}
                                    onClick={handleGetVerificationCode}
                                >
                                    {countdown > 0 ? `${countdown}s` : t('pages.login.getCode')}
                                </Button>
                            </div>
                        </div>
                    </Form.Item>

                    <Form.Item>
                        <div className="flex justify-center">
                            <Button
                                block
                                color="primary"
                                size="large"
                                type="submit"
                            >
                                {t('pages.login.blacklistSmsVerification.ok')}
                            </Button>
                        </div>
                    </Form.Item>
                </Form>

            </div>
        </div>
    );
};

export default BlacklistSmsVerificationNativePage;
